import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import Icon from './Icon'

/**
 * Confirmation riche, en remplacement de window.confirm().
 *
 * window.confirm() gèle la page, n'affiche qu'une phrase et ne peut pas montrer
 * ce sur quoi porte l'action (quelle opération, quel montant, quelle date), ce
 * qui est justement l'information dont on a besoin avant de supprimer.
 *
 * Usage : const ok = await confirm({ title, description, details, confirmLabel, danger })
 */
const ConfirmContext = createContext(null)

export function ConfirmProvider({ children }) {
  const [request, setRequest] = useState(null)
  const resolverRef = useRef(null)
  const confirmButtonRef = useRef(null)
  const cancelButtonRef = useRef(null)

  const close = useCallback((result) => {
    setRequest(null)
    if (resolverRef.current) {
      resolverRef.current(result)
      resolverRef.current = null
    }
  }, [])

  const confirm = useCallback((options) => {
    setRequest(options)
    return new Promise((resolve) => {
      resolverRef.current = resolve
    })
  }, [])

  // Échap annule : un raccourci attendu de toute boîte de dialogue.
  useEffect(() => {
    if (!request) return undefined
    const onKeyDown = (e) => {
      if (e.key === 'Escape') close(false)
    }
    document.addEventListener('keydown', onKeyDown)
    // Sur une action destructive, le focus part sur Annuler : Entrée ne
    // confirme pas la suppression par accident.
    const initial = request.danger ? cancelButtonRef.current : confirmButtonRef.current
    initial?.focus()
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [request, close])

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {request && (
        <div
          className="modal-backdrop z-[150]"
          onClick={() => close(false)}
          role="presentation"
        >
          <div
            className="modal-panel"
            onClick={(e) => e.stopPropagation()}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
          >
            <div className="modal-body">
              <div className="flex items-start gap-3.5">
                {/* Pastille d'intention : rouge pour une destruction, neutre
                    sinon. C'est le seul signal couleur du dialogue. */}
                <span
                  aria-hidden="true"
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                    request.danger
                      ? 'bg-negative-subtle text-negative'
                      : 'bg-accent-subtle text-accent'
                  }`}
                >
                  <Icon name={request.danger ? 'trash' : 'help'} size={18} />
                </span>
                <div className="min-w-0 flex-1 pt-1">
                  <h2 id="confirm-title" className="text-lg font-semibold text-ink">
                    {request.title}
                  </h2>
                  {request.description && (
                    <p className="mt-1.5 text-base text-ink-secondary">{request.description}</p>
                  )}
                </div>
              </div>

              {/* Rappel de ce sur quoi porte l'action : c'est ce qui manque à window.confirm(). */}
              {Array.isArray(request.details) && request.details.length > 0 && (
                <dl className="mt-4 rounded-lg border border-line bg-surface-sunken px-4 py-1">
                  {request.details.map((detail) => (
                    <div key={detail.label} className="data-row">
                      <dt className="data-label text-sm">{detail.label}</dt>
                      <dd className={`data-value break-words ${detail.className || ''}`}>
                        {detail.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}

              {request.warning && (
                <div className="callout-warning mt-4">
                  <Icon name="alert" size={17} className="mt-0.5 shrink-0" />
                  <p>{request.warning}</p>
                </div>
              )}
            </div>

            <div className="modal-footer justify-end">
              <button
                type="button"
                ref={cancelButtonRef}
                onClick={() => close(false)}
                className="btn-secondary"
              >
                {request.cancelLabel || 'Annuler'}
              </button>
              <button
                ref={confirmButtonRef}
                type="button"
                onClick={() => close(true)}
                className={request.danger ? 'btn-danger' : 'btn-primary'}
              >
                {request.confirmLabel || 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  )
}

export function useConfirm() {
  const context = useContext(ConfirmContext)
  if (!context) {
    // Repli sur la confirmation native si le provider est absent.
    return async (options) => window.confirm(options?.title || 'Confirmer ?')
  }
  return context
}
